import { DateTime } from 'luxon';

// Business timezone - all business logic uses this
export const BUSINESS_TIMEZONE = 'America/Chicago';

// Never set global defaults to avoid surprises in SSR/CSR
// Settings.defaultZone = null; // Explicitly avoid global defaults

/**
 * Parse ISO-8601 string with Z/offset and validate
 * @param {string} isoString - ISO-8601 string with timezone info
 * @returns {DateTime} - Validated DateTime in UTC
 * @throws {Error} - If invalid format or timezone
 */
export function parseUTC(isoString) {
  if (!isoString) {
    throw new Error('ISO string is required');
  }
  
  const dt = DateTime.fromISO(isoString, { zone: 'utc' });
  
  if (!dt.isValid) {
    throw new Error(`Invalid ISO string: ${dt.invalidReason} - ${dt.invalidExplanation}`);
  }
  
  return dt;
}

/**
 * Convert Chicago wall-time to UTC for storage
 * @param {Object|DateTime} input - DateTime object with Chicago time or DateTime object
 * @returns {DateTime|string} - DateTime in UTC or UTC ISO string
 */
export function chicagoToUTC(input) {
  if (!input) return null;
  
  try {
    let dt;
    
    // If input is a DateTime object, use it directly
    if (DateTime.isDateTime(input)) {
      dt = input;
    } else {
      // If input is an object, create DateTime from it
      dt = DateTime.fromObject(input, { zone: BUSINESS_TIMEZONE });
    }
    
    if (!dt.isValid) {
      throw new Error(`Invalid Chicago time: ${dt.invalidReason} - ${dt.invalidExplanation}`);
    }
    
    const utc = dt.toUTC();
    return utc.toISO();
  } catch (error) {
    console.error('Error converting Chicago time to UTC:', error);
    return null;
  }
}

/**
 * Convert UTC (from DB) to Chicago for business logic
 * @param {string} isoUtc - ISO string in UTC
 * @returns {DateTime} - DateTime in Chicago timezone
 */
export function utcToChicago(isoUtc) {
  return parseUTC(isoUtc).setZone(BUSINESS_TIMEZONE);
}

/**
 * Format DateTime for display in user's locale
 * @param {DateTime} dt - DateTime object
 * @param {Object} options - Luxon formatting options
 * @returns {string} - Formatted string
 */
export function formatForDisplay(dt, options = {}) {
  const defaultOptions = {
    ...DateTime.DATETIME_MED_WITH_SECONDS,
    ...options
  };
  
  return dt.toLocaleString(defaultOptions);
}

/**
 * Get current time in UTC for database operations
 * @returns {DateTime} - Current time in UTC
 */
export function nowUTC() {
  return DateTime.utc();
}

/**
 * Get current time in Chicago for business logic
 * @returns {DateTime} - Current time in Chicago
 */
export function nowChicago() {
  return DateTime.now().setZone(BUSINESS_TIMEZONE);
}

/**
 * Calculate the current week number based on start date, accounting for break periods
 * Week 1 ends at start_date + 1 week
 * Week 2 starts after Week 1 ends, and so on
 * Break periods are subtracted from elapsed time
 * @param {string|DateTime} startDate - Start date/time in UTC or DateTime object
 * @param {DateTime} [currentTime] - Optional current time (defaults to now)
 * @param {Array} [breakPeriods] - Optional array of break periods {start: DateTime, end: DateTime}
 * @returns {number} - Current week number (1-based), or 0 if start date hasn't passed
 */
export function getCurrentWeekNumber(startDate, currentTime = null, breakPeriods = []) {
  if (!startDate) return 0;
  
  try {
    const start = DateTime.isDateTime(startDate)
      ? startDate.setZone(BUSINESS_TIMEZONE)
      : utcToChicago(startDate);
    
    const now = currentTime
      ? (DateTime.isDateTime(currentTime) ? currentTime.setZone(BUSINESS_TIMEZONE) : utcToChicago(currentTime))
      : nowChicago();
    
    if (!start.isValid || !now.isValid) {
      return 0;
    }
    
    // If current time is before start date, return 0
    if (now < start) {
      return 0;
    }
    
    // Calculate the difference in milliseconds
    let elapsedMs = now.diff(start, 'milliseconds').milliseconds;
    
    // Subtract break periods that have occurred
    if (breakPeriods && breakPeriods.length > 0) {
      for (const breakPeriod of breakPeriods) {
        const breakStart = DateTime.isDateTime(breakPeriod.start)
          ? breakPeriod.start.setZone(BUSINESS_TIMEZONE)
          : utcToChicago(breakPeriod.start);
        
        const breakEnd = DateTime.isDateTime(breakPeriod.end)
          ? breakPeriod.end.setZone(BUSINESS_TIMEZONE)
          : utcToChicago(breakPeriod.end);
        
        if (breakStart.isValid && breakEnd.isValid) {
          if (breakEnd <= now) {
            // Break has fully passed - subtract entire break duration
            const breakDuration = breakEnd.diff(breakStart, 'milliseconds').milliseconds;
            elapsedMs -= breakDuration;
          } else if (breakStart <= now && breakEnd > now) {
            // Currently in a break - subtract time from break start to now
            const partialBreak = now.diff(breakStart, 'milliseconds').milliseconds;
            elapsedMs -= partialBreak;
          }
          // Future breaks don't affect current week calculation
        }
      }
    }
    
    // Calculate weeks (7 days = 1 week)
    const diffWeeks = Math.floor(elapsedMs / (7 * 24 * 60 * 60 * 1000));
    
    // Week number is 1-based, so add 1
    return diffWeeks + 1;
  } catch (error) {
    console.error('Error calculating current week number:', error);
    return 0;
  }
}

/**
 * Get the end date/time for a specific week, accounting for break periods
 * @param {string|DateTime} startDate - Start date/time in UTC or DateTime object
 * @param {number} weekNumber - Week number (1-based)
 * @param {Array} [breakPeriods] - Optional array of break periods {start: DateTime, end: DateTime}
 * @returns {DateTime} - End date/time for the week in Chicago timezone
 */
export function getWeekEndDate(startDate, weekNumber, breakPeriods = []) {
  if (!startDate || !weekNumber || weekNumber < 1) return null;
  
  try {
    const start = DateTime.isDateTime(startDate)
      ? startDate.setZone(BUSINESS_TIMEZONE)
      : utcToChicago(startDate);
    
    if (!start.isValid) return null;
    
    // Start with the naive end date (no breaks)
    let endDate = start.plus({ weeks: weekNumber });
    
    // Add break durations that occur before or during this week
    if (breakPeriods && breakPeriods.length > 0) {
      const sortedBreaks = [...breakPeriods].sort((a, b) => {
        const aStart = DateTime.isDateTime(a.start) ? a.start : utcToChicago(a.start);
        const bStart = DateTime.isDateTime(b.start) ? b.start : utcToChicago(b.start);
        return aStart - bStart;
      });
      
      for (const breakPeriod of sortedBreaks) {
        const breakStart = DateTime.isDateTime(breakPeriod.start)
          ? breakPeriod.start.setZone(BUSINESS_TIMEZONE)
          : utcToChicago(breakPeriod.start);
        
        const breakEnd = DateTime.isDateTime(breakPeriod.end)
          ? breakPeriod.end.setZone(BUSINESS_TIMEZONE)
          : utcToChicago(breakPeriod.end);
        
        if (breakStart.isValid && breakEnd.isValid && breakStart < endDate) {
          // This break affects the week end date
          const breakDuration = breakEnd.diff(breakStart);
          endDate = endDate.plus(breakDuration);
        }
      }
    }
    
    return endDate;
  } catch (error) {
    console.error('Error calculating week end date:', error);
    return null;
  }
}

/**
 * Check if a week number is available for submission, accounting for break periods
 * @param {string|DateTime} startDate - Start date/time in UTC or DateTime object
 * @param {number} weekNumber - Week number to check
 * @param {DateTime} [currentTime] - Optional current time (defaults to now)
 * @param {Array} [breakPeriods] - Optional array of break periods {start: DateTime, end: DateTime}
 * @returns {Object} - { available: boolean, isLate: boolean, reason: string }
 */
export function isWeekAvailableForSubmission(startDate, weekNumber, currentTime = null, breakPeriods = []) {
  if (!startDate || !weekNumber || weekNumber < 1) {
    return { available: false, isLate: false, reason: 'Invalid week number' };
  }
  
  const currentWeek = getCurrentWeekNumber(startDate, currentTime, breakPeriods);
  
  // Can't submit for future weeks
  if (weekNumber > currentWeek) {
    return {
      available: false,
      isLate: false,
      reason: `Week ${weekNumber} is not available yet. Current week is ${currentWeek || 'not started'}.`
    };
  }
  
  // Can submit for current week or past weeks
  // Past weeks are considered late
  const isLate = weekNumber < currentWeek;
  
  return {
    available: true,
    isLate,
    reason: isLate ? `Week ${weekNumber} submission is late` : `Week ${weekNumber} is current`
  };
}

/**
 * Check if a submission is late based on submission time vs week-specific due date
 * This is different from isWeekAvailableForSubmission which checks if a week is past
 * @param {string|DateTime} startDate - Start date/time in UTC or DateTime object
 * @param {number} weekNumber - Week number (1-based)
 * @param {string|DateTime} submittedAt - Submission time in UTC or DateTime object
 * @param {string|DateTime} [assignmentDueDate] - Optional assignment due_date (for Week 1)
 * @param {Array} [breakPeriods] - Optional array of break periods {start: DateTime, end: DateTime}
 * @returns {boolean} - True if submission is late
 */
export function isSubmissionLate(startDate, weekNumber, submittedAt, assignmentDueDate = null, breakPeriods = []) {
  if (!startDate || !weekNumber || !submittedAt) {
    return false;
  }
  
  try {
    const start = DateTime.isDateTime(startDate)
      ? startDate.setZone(BUSINESS_TIMEZONE)
      : utcToChicago(startDate);
    
    const submitted = DateTime.isDateTime(submittedAt)
      ? submittedAt.setZone(BUSINESS_TIMEZONE)
      : utcToChicago(submittedAt);
    
    if (!start.isValid || !submitted.isValid) {
      return false;
    }
    
    // Calculate week-specific due date
    let weekDueDate;
    if (weekNumber === 1 && assignmentDueDate) {
      // For Week 1, use assignment.due_date if provided
      weekDueDate = DateTime.isDateTime(assignmentDueDate)
        ? assignmentDueDate.setZone(BUSINESS_TIMEZONE)
        : utcToChicago(assignmentDueDate);
    } else {
      // For other weeks, calculate from start_date + week_number weeks, accounting for breaks
      weekDueDate = getWeekEndDate(start, weekNumber, breakPeriods);
    }
    
    if (!weekDueDate || !weekDueDate.isValid) {
      return false;
    }
    
    // Submission is late if submitted AFTER the week's due date
    return submitted > weekDueDate;
  } catch (error) {
    console.error('Error calculating if submission is late:', error);
    return false;
  }
}

/**
 * Get current time in Chicago (alias for nowChicago)
 * @returns {DateTime} - Current time in Chicago
 */
export function getChicagoNow() {
  return nowChicago();
}

/**
 * Parse UTC timestamp and convert to Chicago (alias for utcToChicago)
 * @param {string} utcTimestamp - UTC timestamp from database
 * @returns {DateTime} - DateTime in Chicago timezone
 */
export function parseUTCToChicago(utcTimestamp) {
  return utcToChicago(utcTimestamp);
}

/**
 * Format UTC timestamp for display in Chicago timezone (alias for formatForDisplay with utcToChicago)
 * @param {string} utcTimestamp - UTC timestamp from database
 * @param {string} format - Luxon format string
 * @returns {string} - Formatted date string in Chicago timezone
 */
export function formatUTCToChicago(utcTimestamp, format = 'MMM d, yyyy \'at\' h:mm a z') {
  if (!utcTimestamp) return 'No date';
  
  try {
    const chicago = utcToChicago(utcTimestamp);
    return chicago.toFormat(format);
  } catch (error) {
    console.error('Error formatting UTC timestamp to Chicago timezone:', error);
    return 'Invalid date';
  }
}

/**
 * Format date for display in Chicago timezone - handles both Date objects and ISO strings
 * @param {Date|string} dateInput - Date object or ISO string from database
 * @param {string} format - Luxon format string
 * @returns {string} - Formatted date string in Chicago timezone
 */
export function formatDateForDisplay(dateInput, format = 'M/d/yyyy') {
  if (!dateInput) return 'No date';
  
  try {
    let chicago;
    
    if (dateInput instanceof Date) {
      // Handle Date objects from database
      // Check if this is a date-only value (time is 00:00:00 UTC)
      const isoString = dateInput.toISOString();
      
      if (isoString.endsWith('T00:00:00.000Z')) {
        // This is a date-only value, extract just the date part
        const dateOnly = isoString.split('T')[0];
        chicago = DateTime.fromISO(dateOnly);
      } else {
        // This is a datetime value, convert from UTC to Chicago
        chicago = DateTime.fromJSDate(dateInput, { zone: 'utc' }).setZone(BUSINESS_TIMEZONE);
      }
    } else if (typeof dateInput === 'string') {
      // Handle ISO strings
      if (dateInput.includes('T')) {
        // Check if this is a date-only value (ends with T00:00:00.000Z)
        if (dateInput.endsWith('T00:00:00.000Z')) {
          // This is a date-only value, extract just the date part
          const dateOnly = dateInput.split('T')[0];
          chicago = DateTime.fromISO(dateOnly);
        } else {
          // ISO string with actual time
          chicago = DateTime.fromISO(dateInput, { zone: 'utc' }).setZone(BUSINESS_TIMEZONE);
        }
      } else {
        // Date only string (YYYY-MM-DD) - treat as date-only, no timezone conversion
        chicago = DateTime.fromISO(dateInput);
      }
    } else {
      throw new Error('Invalid date input type');
    }
    
    const result = chicago.toFormat(format);
    
    return result;
  } catch (error) {
    console.error('Error formatting date for display:', error);
    return 'Invalid date';
  }
}

/**
 * Format UTC time for API responses (ISO-8601 with Z)
 * @param {DateTime} dt - DateTime object
 * @returns {string} - ISO string in UTC
 */
export function formatForAPI(dt) {
  return dt.toUTC().toISO();
}

/**
 * Create DateTime from Chicago business hours
 * @param {Object} obj - Object with hour, minute, etc.
 * @param {string} date - Date string (YYYY-MM-DD)
 * @returns {DateTime} - DateTime in Chicago, converted to UTC
 */
export function createBusinessTime(obj, date) {
  const chicagoTime = DateTime.fromObject(
    { ...obj, year: date.split('-')[0], month: date.split('-')[1], day: date.split('-')[2] },
    { zone: BUSINESS_TIMEZONE }
  );
  
  if (!chicagoTime.isValid) {
    throw new Error(`Invalid business time: ${chicagoTime.invalidReason} - ${chicagoTime.invalidExplanation}`);
  }
  
  return chicagoTime.toUTC();
}

/**
 * Handle DST edge cases
 * @param {DateTime} dt - DateTime that might be in DST transition
 * @param {string} policy - 'forward' for spring, 'backward' for fall
 * @returns {DateTime} - Adjusted DateTime
 */
export function handleDSTEdge(dt, policy = 'forward') {
  if (!dt.isValid) {
    if (dt.invalidReason === 'unsupported zone') {
      // Handle nonexistent time (spring forward)
      if (policy === 'forward') {
        console.warn(`DST edge case: Nonexistent time ${dt.toISO()}, shifting forward`);
        return dt.plus({ minutes: 1 });
      }
    }
    // Handle ambiguous time (fall back)
    if (policy === 'backward') {
      console.warn(`DST edge case: Ambiguous time ${dt.toISO()}, choosing earlier`);
      return dt.minus({ hours: 1 });
    }
  }
  
  return dt;
}

/**
 * Get timezone info for display
 * @param {DateTime} dt - DateTime object
 * @returns {Object} - Timezone information
 */
export function getTimezoneInfo(dt) {
  const chicago = dt.setZone(BUSINESS_TIMEZONE);
  return {
    offset: chicago.offsetNameShort,
    zone: chicago.zoneName,
    utc: dt.toUTC().toISO(),
    chicago: chicago.toISO(),
    offsetMinutes: chicago.offset
  };
}

/**
 * Get time remaining for a due date
 * @param {string} utcTimestamp - UTC timestamp from database
 * @returns {string} - Human-readable time remaining
 */
export function getTimeRemaining(utcTimestamp) {
  if (!utcTimestamp) return 'No due date';
  
  try {
    const chicago = utcToChicago(utcTimestamp);
    const now = nowChicago();
    const diff = chicago.diff(now, ['days', 'hours', 'minutes']);
    
    if (diff.as('milliseconds') < 0) return 'Overdue';
    
    const days = Math.floor(diff.days);
    const hours = Math.floor(diff.hours);
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
    return 'Due soon';
  } catch (error) {
    console.error('Error getting time remaining:', error);
    return 'Invalid date';
  }
}

/**
 * Check if a due date is overdue
 * @param {string} utcTimestamp - UTC timestamp from database
 * @returns {boolean} - True if overdue
 */
export function isOverdue(utcTimestamp) {
  if (!utcTimestamp) return false;
  
  try {
    const chicago = utcToChicago(utcTimestamp);
    const now = nowChicago();
    return now > chicago;
  } catch (error) {
    console.error('Error checking if overdue:', error);
    return false;
  }
}

/**
 * Check if a due date is due soon (within 24 hours)
 * @param {string} utcTimestamp - UTC timestamp from database
 * @returns {boolean} - True if due soon
 */
export function isDueSoon(utcTimestamp) {
  if (!utcTimestamp) return false;
  
  try {
    const chicago = utcToChicago(utcTimestamp);
    const now = nowChicago();
    const diff = chicago.diff(now, 'hours');
    
    return diff.hours > 0 && diff.hours <= 24;
  } catch (error) {
    console.error('Error checking if due soon:', error);
    return false;
  }
}

/**
 * Get current date string in Chicago timezone (YYYY-MM-DD format)
 * @returns {string} Current date in YYYY-MM-DD format
 */
export function getChicagoDateString() {
  return nowChicago().toISODate();
}

/**
 * Create a Chicago DateTime from date and time strings
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} timeStr - Time string in HH:mm format
 * @returns {DateTime} DateTime object in Chicago timezone
 */
export function createChicagoDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  
  try {
    const isoString = `${dateStr}T${timeStr}:00`;
    const dt = DateTime.fromISO(isoString, { zone: BUSINESS_TIMEZONE });
    
    if (!dt.isValid) {
      throw new Error(`Invalid date/time: ${dt.invalidReason} - ${dt.invalidExplanation}`);
    }
    
    return dt;
  } catch (error) {
    console.error('Error creating Chicago DateTime:', error);
    return null;
  }
}


/**
 * Get Chicago timezone abbreviation
 * @returns {string} Timezone abbreviation (CST/CDT)
 */
export function getChicagoTimezoneAbbr() {
  return nowChicago().toFormat('z');
}

/**
 * Get current time string in Chicago timezone (HH:mm format)
 * @returns {string} Current time in HH:mm format
 */
export function getChicagoTimeString() {
  return nowChicago().toFormat('HH:mm');
}


/**
 * Validate and normalize time input
 * @param {string} timeString - Time string to validate
 * @returns {Object} - Normalized time object
 */
export function validateTimeInput(timeString) {
  if (!timeString) {
    throw new Error('Time string is required');
  }
  
  // Try parsing as ISO first
  let dt = DateTime.fromISO(timeString, { zone: 'utc' });
  
  if (!dt.isValid) {
    // Try parsing as local time in Chicago
    dt = DateTime.fromFormat(timeString, 'HH:mm', { zone: BUSINESS_TIMEZONE });
  }
  
  if (!dt.isValid) {
    throw new Error(`Invalid time format: ${dt.invalidReason} - ${dt.invalidExplanation}`);
  }
  
  return {
    utc: dt.toUTC().toISO(),
    chicago: dt.setZone(BUSINESS_TIMEZONE).toISO(),
    isValid: dt.isValid
  };
}

/**
 * Create relative time display
 * @param {DateTime} dt - DateTime to compare
 * @param {DateTime} base - Base time (defaults to now)
 * @returns {string} - Relative time string
 */
export function getRelativeTime(dt, base = nowChicago()) {
  const chicago = dt.setZone(BUSINESS_TIMEZONE);
  const baseChicago = base.setZone(BUSINESS_TIMEZONE);
  
  const diff = chicago.diff(baseChicago, ['days', 'hours', 'minutes']);
  
  if (Math.abs(diff.days) >= 1) {
    return `${Math.abs(diff.days)} day${Math.abs(diff.days) !== 1 ? 's' : ''} ${diff.days > 0 ? 'from now' : 'ago'}`;
  } else if (Math.abs(diff.hours) >= 1) {
    return `${Math.abs(diff.hours)} hour${Math.abs(diff.hours) !== 1 ? 's' : ''} ${diff.hours > 0 ? 'from now' : 'ago'}`;
  } else {
    return `${Math.abs(diff.minutes)} minute${Math.abs(diff.minutes) !== 1 ? 's' : ''} ${diff.minutes > 0 ? 'from now' : 'ago'}`;
  }
}

/**
 * Format time with timezone info for tooltips
 * @param {DateTime} dt - DateTime object
 * @returns {Object} - Formatted time with timezone info
 */
export function formatWithTimezoneInfo(dt) {
  const chicago = dt.setZone(BUSINESS_TIMEZONE);
  const utc = dt.toUTC();
  
  return {
    display: chicago.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS),
    timezone: `${chicago.offsetNameShort} / UTC${chicago.offset < 0 ? '' : '+'}${chicago.offset / 60}`,
    utc: utc.toISO(),
    chicago: chicago.toISO(),
    offset: chicago.offset
  };
}

/**
 * Validate database timestamp format
 * @param {string} timestamp - Database timestamp
 * @returns {boolean} - Whether timestamp is valid UTC
 */
export function validateDBTimestamp(timestamp) {
  try {
    const dt = DateTime.fromISO(timestamp, { zone: 'utc' });
    return dt.isValid && dt.toISO().endsWith('Z');
  } catch (error) {
    return false;
  }
}

/**
 * Convert database timestamp to Chicago time for display
 * @param {string} dbTimestamp - Database timestamp in UTC
 * @returns {string} - Formatted time in Chicago
 */
export function formatDBTimestampForDisplay(dbTimestamp) {
  try {
    const utc = parseUTC(dbTimestamp);
    const chicago = utc.setZone(BUSINESS_TIMEZONE);
    return chicago.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Invalid date';
  }
}

/**
 * Create accessible time element data
 * @param {string} isoUtc - UTC ISO string
 * @returns {Object} - Data for <time> element
 */
export function createAccessibleTimeData(isoUtc) {
  const utc = parseUTC(isoUtc);
  const chicago = utc.setZone(BUSINESS_TIMEZONE);
  
  return {
    dateTime: isoUtc, // UTC ISO for screen readers
    display: chicago.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS),
    timezone: chicago.offsetNameShort,
    utc: isoUtc
  };
}

/**
 * Validate API timestamp input (must end with Z)
 * @param {string} timestamp - Timestamp from API
 * @returns {boolean} - Whether timestamp is valid UTC
 */
export function validateAPITimestamp(timestamp) {
  if (!timestamp || typeof timestamp !== 'string') {
    return false;
  }
  
  // Must end with Z for UTC
  if (!timestamp.endsWith('Z')) {
    return false;
  }
  
  try {
    const dt = DateTime.fromISO(timestamp, { zone: 'utc' });
    return dt.isValid;
  } catch (error) {
    return false;
  }
}


/**
 * Format time string to 12-hour format with AM/PM
 * @param {string|Date} timeInput - Time string in HH:mm:ss, HH:mm format, or datetime string/Date object
 * @returns {string} - Formatted time string (e.g., "8:00 PM")
 */
export function formatTimeTo12Hour(timeInput) {
  if (!timeInput) return '';
  
  try {
    let timeString;
    
    // Handle different input types
    if (timeInput instanceof Date) {
      // Convert Date object to Chicago time and extract time
      const chicago = DateTime.fromJSDate(timeInput, { zone: 'utc' }).setZone(BUSINESS_TIMEZONE);
      timeString = chicago.toFormat('HH:mm:ss');
    } else if (typeof timeInput === 'string') {
      if (timeInput.includes('T')) {
        // ISO datetime string - convert to Chicago time and extract time
        const chicago = DateTime.fromISO(timeInput, { zone: 'utc' }).setZone(BUSINESS_TIMEZONE);
        timeString = chicago.toFormat('HH:mm:ss');
      } else {
        // Time string (HH:mm:ss or HH:mm)
        timeString = timeInput;
      }
    } else {
      return timeInput.toString();
    }
    
    const [hours, minutes] = timeString.split(':');
    const hour12 = hours === '00' ? 12 : hours > 12 ? hours - 12 : hours;
    const period = hours >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${period}`;
  } catch (error) {
    console.error('Error formatting time to 12-hour:', error);
    return timeInput.toString();
  }
}

/**
 * Format time range for display
 * @param {string} startTime - Start time string
 * @param {string} endTime - End time string
 * @returns {string} - Formatted time range (e.g., "8:00 PM - 9:30 PM")
 */
export function formatTimeRange(startTime, endTime) {
  if (!startTime || !endTime) return '';
  return `${formatTimeTo12Hour(startTime)} - ${formatTimeTo12Hour(endTime)}`;
}

// Export commonly used formats
export const FORMATS = {
  API: 'yyyy-MM-dd\'T\'HH:mm:ss.SSS\'Z\'',
  DISPLAY: DateTime.DATETIME_MED_WITH_SECONDS,
  DATE_ONLY: DateTime.DATE_MED,
  TIME_ONLY: DateTime.TIME_SIMPLE,
  ISO: 'yyyy-MM-dd\'T\'HH:mm:ss.SSSZ',
  LEGACY: 'yyyy-MM-dd HH:mm:ss'
};

// Export timezone constants
export const TIMEZONES = {
  UTC: 'utc',
  BUSINESS: BUSINESS_TIMEZONE
};

/**
 * Convert UTC timestamp to user's local timezone for display
 * @param {string|Date} utcTimestamp - UTC timestamp (ISO string or Date object)
 * @param {string} format - Luxon format string (default: 'MMM d, yyyy \'at\' h:mm a')
 * @returns {string} - Formatted string in user's local timezone
 */
export function formatUTCToLocal(utcTimestamp, format = 'MMM d, yyyy \'at\' h:mm a') {
  if (!utcTimestamp) return 'No date';
  
  try {
    let dt;
    
    if (utcTimestamp instanceof Date) {
      dt = DateTime.fromJSDate(utcTimestamp, { zone: 'utc' });
    } else if (typeof utcTimestamp === 'string') {
      dt = DateTime.fromISO(utcTimestamp, { zone: 'utc' });
    } else {
      throw new Error('Invalid timestamp type');
    }
    
    if (!dt.isValid) {
      throw new Error(`Invalid timestamp: ${dt.invalidReason}`);
    }
    
    // Convert to user's local timezone (browser will use system timezone)
    const localDt = dt.toLocal();
    
    return localDt.toFormat(format);
  } catch (error) {
    console.error('Error formatting UTC to local time:', error);
    return 'Invalid date';
  }
}

/**
 * Format time for display in user's local timezone
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted time string
 */
export function formatTimeToLocal(utcTimestamp, options = {}) {
  if (!utcTimestamp) return 'No time';
  
  try {
    let date;
    
    if (utcTimestamp instanceof Date) {
      date = utcTimestamp;
    } else if (typeof utcTimestamp === 'string') {
      date = new Date(utcTimestamp);
    } else {
      throw new Error('Invalid timestamp type');
    }
    
    if (isNaN(date.getTime())) {
      throw new Error('Invalid timestamp');
    }
    
    const defaultOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...options
    };
    
    return date.toLocaleTimeString(undefined, defaultOptions);
  } catch (error) {
    console.error('Error formatting time to local:', error);
    return 'Invalid time';
  }
}
